import type { GameState, Passenger } from '../types.js';

/**
 * The service score.
 *
 * Accumulated as the run happens rather than computed at the end, so a run that
 * is abandoned halfway is still scored honestly — and so the player can watch it
 * move while they play.
 *
 * The numbers exist to create *conflicts*, not to be realistic. Every term below
 * should be defensible as "this is why the player has to choose":
 *
 *   - Waiting costs points, so leaving anyone behind is never free.
 *   - A VIP earns double and loses points twice as fast, so serving them is
 *     worth a detour — up to a point the player has to judge.
 *   - A courier pays well on time and poorly late, so missing a deadline is a
 *     survivable mistake rather than a trap. That is what keeps it a decision.
 *   - Someone who gives up costs more than they would have earned, so ignoring a
 *     floor is a real loss rather than a rounding error.
 */
export const SERVICE = {
  /** Awarded for getting someone where they were going. */
  deliveryBase: 100,
  /** Points lost per second spent waiting on a floor. */
  waitPenaltyPerSecond: 4,
  /** No amount of waiting can make a delivery worth less than this. */
  minDeliveryValue: 20,
  /** VIPs earn double and are penalised double. */
  vipMultiplier: 2,
  courierOnTimeBonus: 60,
  /** Charged when patience runs out and they walk away. */
  abandonPenalty: 120,
  /** Charged when a delivery deadline passes and the job is lost. */
  failedDeliveryPenalty: 150,
} as const;

/** What one delivery was worth, and why. Used for the results breakdown. */
export interface DeliveryValue {
  readonly base: number;
  readonly waitPenalty: number;
  readonly courierBonus: number;
  readonly total: number;
}

export function valueOfDelivery(passenger: Passenger, deliveredMs: number): DeliveryValue {
  const multiplier = passenger.vip ? SERVICE.vipMultiplier : 1;
  const waitMs = Math.max(0, (passenger.boardedMs ?? deliveredMs) - passenger.spawnMs);

  const base = SERVICE.deliveryBase * multiplier;
  const rawPenalty = (waitMs / 1000) * SERVICE.waitPenaltyPerSecond * multiplier;
  // Clamp so a very late delivery is still better than no delivery at all —
  // otherwise the optimal play becomes abandoning anyone you fell behind on.
  const waitPenalty = Math.min(rawPenalty, base - SERVICE.minDeliveryValue);

  // A late delivery is not a thing that can happen: the deadline removes the
  // courier before it arrives. So this is a bonus with no matching penalty —
  // the penalty lives in `chargeFailedDelivery`, where the loss actually occurs.
  const courierBonus =
    passenger.deadlineMs !== null && deliveredMs <= passenger.deadlineMs
      ? SERVICE.courierOnTimeBonus
      : 0;

  return {
    base,
    waitPenalty: Math.round(waitPenalty),
    courierBonus,
    total: Math.round(base - waitPenalty + courierBonus),
  };
}

export function creditDelivery(state: GameState, passenger: Passenger): void {
  const value = valueOfDelivery(passenger, state.elapsedMs);
  state.score.service += value.total;

  if (passenger.vip) state.score.vipsServed += 1;
  // Couriers only: a crew's clock is the emergency's (ADR-0029), not a delivery.
  if (passenger.deadlineMs !== null && !passenger.critical) {
    if (state.elapsedMs <= passenger.deadlineMs) state.score.couriersOnTime += 1;
    else state.score.couriersLate += 1;
  }
}

/**
 * What losing this passenger costs the service score: walking away, being
 * turned away at a full landing, or a courier's deadline passing.
 *
 * A missed deadline costs more than someone simply walking away, because it was
 * avoidable twice: you could have gone sooner, or not taken the job at all.
 */
export function costOfLoss(
  passenger: Passenger,
  reason: 'gave-up' | 'deadline' | 'no-room',
): number {
  const multiplier = passenger.vip ? SERVICE.vipMultiplier : 1;
  const penalty = reason === 'deadline' ? SERVICE.failedDeliveryPenalty : SERVICE.abandonPenalty;
  return penalty * multiplier;
}

export function chargeGaveUp(state: GameState, passenger: Passenger): void {
  state.score.service -= costOfLoss(passenger, 'gave-up');
  state.score.abandoned += 1;
}

export function chargeFailedDelivery(state: GameState, passenger: Passenger): void {
  state.score.service -= costOfLoss(passenger, 'deadline');
  if (!passenger.critical) state.score.couriersLate += 1;
}
